import { CHIAVI_ORDINAMENTO } from '../../api/tipi';
import type { OrdineScelto } from '../../componenti/Ordinamento';
/* Lo stato della ricerca vive nell'URL, non in `useState`.
 *
 * Su 1.507.455 righe una ricerca è cara: un aggiornamento di pagina che
 * riportasse a "tutti i backtest ordinati per profitto" costringerebbe a
 * ricomporre a mano sette filtri e a ripagare la query. Nell'URL invece la
 * combinazione è condivisibile, torna indietro col tasto del browser e
 * sopravvive al ricaricamento.
 *
 * Il modello NON è più ricco di quello che il server sa fare: ogni campo qui
 * corrisponde a un parametro reale di `GET /backtester/search`. Un filtro in
 * più — per esempio più strategie insieme — verrebbe ignorato in silenzio e
 * l'utente vedrebbe un risultato diverso da quello che ha chiesto.
 */


/** Le dimensioni di pagina offerte. Oltre 200 il server taglia comunque. */
export const DIMENSIONI_PAGINA = [25, 50, 100, 200] as const;

export interface StatoRicerca {
  /* --- filtri: uno a uno quelli del controller --- */
  strategyName: string | null;
  asset: string | null;
  candleSize: number | null;
  backtestGroupId: number | null;
  /** Semantica di COPERTURA: il backtest deve contenere l'intervallo. */
  startDate: string | null;
  endDate: string | null;
  minTrades: number | null;
  minChange: number | null;
  maxDrawdown: number | null;
  minIntorno: number | null;
  /** Tenuta minima, in PERCENTUALE come si legge a schermo: il server converte nel rapporto. */
  minTenuta: number | null;

  /* --- ordinamento e paginazione: entrambi lato server ---
     L'ordinamento e' una LISTA: su una griglia le parita' sono la norma, e con un criterio solo
     quale configurazione finisca in cima e' arbitrario. Il primo decide, gli altri sciolgono. */
  ordine: OrdineScelto[];
  page: number;
  limit: number;
}

/**
 * L'ordinamento nell'URL: `ordina=strategyChange:desc,walkForwardScore:desc`.
 *
 * Sta nell'URL come tutto il resto della ricerca perche' una combinazione costata una query si
 * condivide e si ritrova col tasto indietro senza ripagarla.
 */
export function leggiOrdine(grezzo: string | null): OrdineScelto[] {
  const voci = (grezzo ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)
    .map((v) => {
      const [chiave, verso] = v.split(':');
      return { chiave, verso: verso?.toUpperCase() === 'ASC' ? 'ASC' : 'DESC' };
    })
    .filter((v) => (CHIAVI_ORDINAMENTO as readonly string[]).includes(v.chiave))
    .slice(0, 3) as OrdineScelto[];
  return voci.length ? voci : [{ chiave: 'strategyChange', verso: 'DESC' }];
}

export function scriviOrdine(ordine: OrdineScelto[]): string {
  return ordine.map((o) => `${o.chiave}:${o.verso.toLowerCase()}`).join(',');
}

export const RICERCA_INIZIALE: StatoRicerca = {
  strategyName: null,
  asset: null,
  candleSize: null,
  backtestGroupId: null,
  startDate: null,
  endDate: null,
  minTrades: null,
  minChange: null,
  maxDrawdown: null,
  minIntorno: null,
  minTenuta: null,
  ordine: [{ chiave: 'strategyChange', verso: 'DESC' }],
  page: 1,
  limit: 50,
};

/** Le chiavi dei soli filtri: servono a contare quelli attivi e ad azzerarli. */
export const CHIAVI_FILTRO = [
  'strategyName',
  'asset',
  'candleSize',
  'backtestGroupId',
  'startDate',
  'endDate',
  'minTrades',
  'minChange',
  'maxDrawdown',
  'minIntorno',
  'minTenuta',
] as const;

export type ChiaveFiltro = (typeof CHIAVI_FILTRO)[number];

function numeroDa(v: string | null): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function leggiDaUrl(p: URLSearchParams): StatoRicerca {
  const ordinaGrezzo = p.get('ordina');
  const limite = numeroDa(p.get('perPagina'));

  return {
    strategyName: p.get('strategia') || null,
    asset: p.get('asset') || null,
    candleSize: numeroDa(p.get('tf')),
    backtestGroupId: numeroDa(p.get('gruppo')),
    startDate: p.get('dal') || null,
    endDate: p.get('al') || null,
    minTrades: numeroDa(p.get('opsMin')),
    minChange: numeroDa(p.get('profittoMin')),
    maxDrawdown: numeroDa(p.get('ddMax')),
    minIntorno: numeroDa(p.get('intornoMin')),
    minTenuta: numeroDa(p.get('tenutaMin')),

    /* Una chiave fuori whitelist il server la ignorerebbe ricadendo su
       `strategyChange` SENZA dirlo: meglio scartarla qui, dove si può almeno
       mostrare l'ordinamento vero. */
    /* Ogni voce e' `chiave:verso`. Le chiavi fuori dalla lista che il server accetta si
       scartano QUI, dove si puo' ancora mostrare l'ordinamento vero: passandole, il server le
       ignorerebbe in silenzio e a schermo comparirebbe un ordine diverso da quello chiesto. */
    ordine: leggiOrdine(ordinaGrezzo),
    page: Math.max(1, numeroDa(p.get('pagina')) ?? 1),
    limit: limite != null && (DIMENSIONI_PAGINA as readonly number[]).includes(limite)
      ? limite
      : RICERCA_INIZIALE.limit,
  };
}

/** Solo ciò che non è predefinito finisce nell'URL: così l'indirizzo di
 *  partenza resta `/backtest` e si legge cosa è stato scelto davvero. */
export function scriviInUrl(s: StatoRicerca): URLSearchParams {
  const p = new URLSearchParams();
  const metti = (chiave: string, valore: string | number | null) => {
    if (valore == null || valore === '') return;
    p.set(chiave, String(valore));
  };

  metti('strategia', s.strategyName);
  metti('asset', s.asset);
  metti('tf', s.candleSize);
  metti('gruppo', s.backtestGroupId);
  metti('dal', s.startDate);
  metti('al', s.endDate);
  metti('opsMin', s.minTrades);
  metti('profittoMin', s.minChange);
  metti('ddMax', s.maxDrawdown);
  metti('intornoMin', s.minIntorno);
  /* `tenutaMin` mancava qui: l'URL è l'UNICA sede dello stato della ricerca, e un
     filtro che non viene scritto non viene nemmeno riletto. Il campo si compilava,
     la bozza cambiava, ma `leggiDaUrl` lo ritrovava `null` e la query restava
     quella di prima. Ogni filtro nuovo va aggiunto in tre punti — il modello,
     `leggiDaUrl` e QUI — o si perde per strada in silenzio. */
  metti('tenutaMin', s.minTenuta);

  if (scriviOrdine(s.ordine) !== scriviOrdine(RICERCA_INIZIALE.ordine)) {
    p.set('ordina', scriviOrdine(s.ordine));
  }
  if (s.page !== 1) p.set('pagina', String(s.page));
  if (s.limit !== RICERCA_INIZIALE.limit) p.set('perPagina', String(s.limit));

  return p;
}

export function filtriAttivi(s: StatoRicerca): ChiaveFiltro[] {
  return CHIAVI_FILTRO.filter((k) => s[k] != null);
}

/** Toglie un solo filtro e riporta a pagina 1. Esiste come funzione perché
 *  `{ ...s, [k]: null }` con una chiave di tipo unione perde il tipo. */
export function rimuoviFiltro(s: StatoRicerca, k: ChiaveFiltro): StatoRicerca {
  const nuovo: StatoRicerca = { ...s, page: 1 };
  (nuovo[k] as unknown) = null;
  return nuovo;
}

export function azzeraFiltri(s: StatoRicerca): StatoRicerca {
  const pulito = { ...s, page: 1 };
  for (const k of CHIAVI_FILTRO) (pulito[k] as unknown) = null;
  return pulito;
}
